<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Training</span>
          <h1 class="hris-page-title">{{ isAdmin ? "Employee Certifications" : "My Certifications" }}</h1>
          <p class="hris-page-subtitle">Track certification issue dates and monitor upcoming expirations.</p>
        </div>
        <div class="row q-gutter-sm">
          <q-btn
            unelevated
            class="hris-q-primary"
            icon="fas fa-plus"
            label="Add Certification"
            @click="openCertDialog"
          />
          <q-btn
            unelevated
            class="hris-q-outline"
            icon="fas fa-sync"
            label="Refresh"
            @click="getCertifications"
          />
        </div>
      </div>

      <div class="hris-grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-bottom: 20px;">
        <div class="hris-stat-card">
          <p class="hris-stat-label">Active</p>
          <p class="hris-stat-value">{{ activeCount }}</p>
        </div>
        <div class="hris-stat-card">
          <p class="hris-stat-label">Expired</p>
          <p class="hris-stat-value">{{ expiredCount }}</p>
        </div>
        <div class="hris-stat-card">
          <p class="hris-stat-label">Expiring within 30 days</p>
          <p class="hris-stat-value">{{ soonCount }}</p>
        </div>
      </div>

      <div class="hris-panel">
        <q-table
          flat
          :data="certifications"
          :columns="columns"
          row-key="id"
          :loading="loading"
          separator="cell"
        >
          <template v-slot:loading>
            <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
              <q-spinner color="green" size="40px" />
              <div>Loading certifications...</div>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="hris-empty full-width">No certifications found.</div>
          </template>
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td v-if="isAdmin" key="employee" :props="props">
                {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
              </q-td>
              <q-td key="certificate_name" :props="props">{{ props.row.certificate_name }}</q-td>
              <q-td key="issuing_organization" :props="props">{{ props.row.issuing_organization }}</q-td>
              <q-td key="issue_date" :props="props">{{ formatDate(props.row.issue_date) }}</q-td>
              <q-td key="expiry_date" :props="props">{{ formatDate(props.row.expiry_date) }}</q-td>
              <q-td key="badge" :props="props">
                <q-badge :class="getBadgeClass(props.row)" class="q-pa-xs">
                  {{ getBadgeLabel(props.row) }}
                </q-badge>
              </q-td>
              <q-td key="actions" :props="props" class="text-center">
                <q-btn
                  flat
                  round
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-edit"
                  @click="openCertDialog(props.row)"
                >
                  <q-tooltip>Edit</q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-trash-alt"
                  @click="confirmDelete(props.row)"
                >
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </div>

      <!-- CERTIFICATION DIALOG -->
      <q-dialog v-model="certDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ certForm.id ? 'Edit Certification' : 'Add Certification' }}</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveCertification">
            <div class="hris-modal-body">
              <div v-if="isAdmin" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="certForm.employee_id"
                    label="Employee (leave blank for self)"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname + ' (' + opt.employee_id + ')' : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    clearable
                    @filter="filterEmployees"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="certForm.certificate_name"
                    label="Certificate Name"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Certificate name is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="certForm.issuing_organization"
                    label="Issuing Organization"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Issuing organization is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="certForm.issue_date"
                    label="Issue Date"
                    outlined
                    dense
                    color="green"
                    mask="date"
                    :rules="[(val) => !!val || 'Issue date is required']"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qIssueDate" transition-show="scale" transition-hide="scale">
                          <q-date v-model="certForm.issue_date" @input="() => $refs.qIssueDate.hide()" />
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="certForm.expiry_date"
                    label="Expiry Date (optional)"
                    outlined
                    dense
                    color="green"
                    mask="date"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qExpiryDate" transition-show="scale" transition-hide="scale">
                          <q-date v-model="certForm.expiry_date" @input="() => $refs.qExpiryDate.hide()" />
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div v-if="isAdmin && certForm.id" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="certForm.status"
                    label="Status"
                    :options="statusOptions"
                    outlined
                    dense
                    color="green"
                  ></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingCert" />
            </div>
          </q-form>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      certifications: [],
      employees: [],
      employeeOptions: [],
      loading: false,
      certDialog: false,
      savingCert: false,
      statusOptions: ["active", "expired", "revoked"],
      certForm: {
        id: null,
        employee_id: null,
        certificate_name: "",
        issuing_organization: "",
        issue_date: "",
        expiry_date: "",
        status: "active"
      }
    };
  },
  computed: {
    isAdmin() {
      const roleId = Number(this.userData.role_id);
      return roleId === 0 || roleId === 1;
    },
    columns() {
      const cols = [];
      if (this.isAdmin) {
        cols.push({ name: "employee", align: "left", label: "Employee", sortable: true });
      }
      cols.push(
        { name: "certificate_name", align: "left", label: "Certificate", sortable: true },
        { name: "issuing_organization", align: "left", label: "Issuing Organization", sortable: true },
        { name: "issue_date", align: "center", label: "Issue Date", sortable: true },
        { name: "expiry_date", align: "center", label: "Expiry Date", sortable: true },
        { name: "badge", align: "center", label: "Status" },
        { name: "actions", align: "center", label: "Actions" }
      );
      return cols;
    },
    activeCount() {
      return this.certifications.filter((c) => c.status === "active" && !c.is_expired).length;
    },
    expiredCount() {
      return this.certifications.filter((c) => c.status === "expired" || c.is_expired).length;
    },
    soonCount() {
      const now = Date.now();
      const in30 = now + 30 * 24 * 60 * 60 * 1000;
      return this.certifications.filter((c) => {
        if (!c.expiry_date || c.is_expired || c.status !== "active") return false;
        const expiry = new Date(c.expiry_date).getTime();
        return expiry >= now && expiry <= in30;
      }).length;
    }
  },
  created() {
    this.getCertifications();
    if (this.isAdmin) {
      this.getEmployees();
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    getHeaders() {
      return {
        headers: { Authorization: "Bearer " + this.token }
      };
    },
    getCertifications() {
      this.loading = true;
      axios
        .get(this.apiUrl + "/lnd/certifications", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.certifications = res.data.data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getEmployees(filter = "") {
      axios
        .get(this.apiUrl + "/employees", {
          headers: { Authorization: "Bearer " + this.token },
          params: {
            status: "ACTIVE",
            countPerPage: 20,
            start: 0,
            filter
          }
        })
        .then((res) => {
          if (res.data.status) {
            const list = res.data.data || [];
            this.employees = list;
            this.employeeOptions = list;
          }
        });
    },
    filterEmployees(val, update) {
      update(() => {
        this.getEmployees(val);
      });
    },
    isExpired(row) {
      if (row.is_expired) return true;
      if (row.status !== "active" || !row.expiry_date) return false;
      return new Date(row.expiry_date).getTime() < Date.now();
    },
    isExpiringSoon(row) {
      if (this.isExpired(row) || row.status !== "active" || !row.expiry_date) return false;
      const now = Date.now();
      const in30 = now + 30 * 24 * 60 * 60 * 1000;
      const expiry = new Date(row.expiry_date).getTime();
      return expiry >= now && expiry <= in30;
    },
    getBadgeLabel(row) {
      if (row.status === "revoked") return "REVOKED";
      if (this.isExpired(row)) return "EXPIRED";
      if (this.isExpiringSoon(row)) return "EXPIRING SOON";
      return "ACTIVE";
    },
    getBadgeClass(row) {
      if (row.status === "revoked") return "bg-grey text-white";
      if (this.isExpired(row)) return "bg-red text-white";
      if (this.isExpiringSoon(row)) return "bg-orange text-white";
      return "bg-green text-white";
    },

    openCertDialog(row) {
      if (row) {
        this.certForm = {
          id: row.id,
          employee_id: row.employee_id,
          certificate_name: row.certificate_name,
          issuing_organization: row.issuing_organization,
          issue_date: row.issue_date,
          expiry_date: row.expiry_date || "",
          status: row.status
        };
      } else {
        this.certForm = {
          id: null,
          employee_id: null,
          certificate_name: "",
          issuing_organization: "",
          issue_date: "",
          expiry_date: "",
          status: "active"
        };
      }
      this.certDialog = true;
    },
    saveCertification() {
      this.savingCert = true;
      const isEdit = !!this.certForm.id;
      const url = isEdit ? "/lnd/certifications/update" : "/lnd/certifications/add";
      axios
        .post(this.apiUrl + url, this.certForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: `Certification ${isEdit ? "updated" : "added"} successfully.` });
            this.certDialog = false;
            this.getCertifications();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to save certification." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.savingCert = false;
        });
    },
    confirmDelete(row) {
      this.$q
        .dialog({
          title: "Confirm Deletion",
          message: `Are you sure you want to delete "${row.certificate_name}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .post(this.apiUrl + `/lnd/certifications/delete/${row.id}`, {}, this.getHeaders())
            .then((res) => {
              if (res.data.status) {
                this.$q.notify({ type: "positive", message: "Certification deleted." });
                this.getCertifications();
              }
            });
        });
    }
  }
};
</script>
